// Simple 8-bit background music synthesizer for the main menu

const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

class BackgroundMusic {
    private audioCtx: AudioContext | null = null;
    private isPlaying = false;
    private nextNoteTime = 0;
    private currentNote = 0;
    private lookahead = 25.0; // ms
    private scheduleAheadTime = 0.1; // s
    private timerID: number | null = null;

    // A catchy 8-bit style arpeggio loop
    // Formatted as [frequency, duration in 16th notes]
    // Tempo context: 120 BPM => 1 beat = 0.5s => 16th note = 0.125s
    private baseTempo = 140; // BPM
    private secondsPerBeat = 60.0 / this.baseTempo;
    private secondsPer16th = this.secondsPerBeat / 4;

    // Notes (C4 = 261.63, E4 = 329.63, G4 = 392.00, C5 = 523.25, D5 = 587.33)
    private sequence: { freq: number; dur: number }[] = [
        { freq: 261.63, dur: 2 }, // C4
        { freq: 329.63, dur: 2 }, // E4
        { freq: 392.00, dur: 2 }, // G4
        { freq: 523.25, dur: 2 }, // C5
        { freq: 392.00, dur: 2 }, // G4
        { freq: 329.63, dur: 2 }, // E4
        { freq: 261.63, dur: 2 }, // C4
        { freq: 392.00, dur: 2 }, // G4
        // second bar
        { freq: 349.23, dur: 2 }, // F4
        { freq: 440.00, dur: 2 }, // A4
        { freq: 523.25, dur: 2 }, // C5
        { freq: 698.46, dur: 2 }, // F5
        { freq: 523.25, dur: 2 }, // C5
        { freq: 440.00, dur: 2 }, // A4
        { freq: 349.23, dur: 2 }, // F4
        { freq: 523.25, dur: 2 }, // C5
    ];

    init() {
        if (!this.audioCtx) {
            this.audioCtx = new AudioCtx();
        }
    }

    play() {
        if (this.isPlaying) return;
        this.init();

        if (!this.audioCtx) return;

        if (this.audioCtx.state === 'suspended') {
            const resume = () => {
                if (this.audioCtx?.state === 'suspended') {
                    this.audioCtx.resume();
                }
                document.removeEventListener('click', resume);
                document.removeEventListener('touchstart', resume);
                document.removeEventListener('keydown', resume);
            };
            document.addEventListener('click', resume);
            document.addEventListener('touchstart', resume);
            document.addEventListener('keydown', resume);
        }

        this.isPlaying = true;
        this.currentNote = 0;
        this.nextNoteTime = this.audioCtx.currentTime + 0.05;
        this.scheduler();
    }

    stop() {
        this.isPlaying = false;
        if (this.timerID !== null) {
            window.clearTimeout(this.timerID);
            this.timerID = null;
        }
    }

    private nextNote() {
        const noteDur = this.sequence[this.currentNote].dur;
        this.nextNoteTime += noteDur * this.secondsPer16th;
        this.currentNote++;
        if (this.currentNote === this.sequence.length) {
            this.currentNote = 0;
        }
    }

    private scheduleNote(freq: number, time: number, durTokens: number) {
        if (!this.audioCtx) return;

        const osc = this.audioCtx.createOscillator();
        const gain = this.audioCtx.createGain();

        osc.type = 'square';
        osc.frequency.value = freq;

        // A tiny envelope for the 8-bit "pluck/blip" sound
        const durInSeconds = durTokens * this.secondsPer16th;
        gain.gain.setValueAtTime(0.03, time); // Low volume
        gain.gain.setTargetAtTime(0.0, time + durInSeconds * 0.5, 0.05);

        osc.connect(gain);
        gain.connect(this.audioCtx.destination);

        osc.start(time);
        osc.stop(time + durInSeconds);
    }

    private scheduler() {
        if (!this.isPlaying || !this.audioCtx) return;

        // While there are notes that will need to play before the next interval,
        // schedule them and advance the pointer.
        while (this.nextNoteTime < this.audioCtx.currentTime + this.scheduleAheadTime) {
            this.scheduleNote(
                this.sequence[this.currentNote].freq,
                this.nextNoteTime,
                this.sequence[this.currentNote].dur
            );
            this.nextNote();
        }

        this.timerID = window.setTimeout(() => this.scheduler(), this.lookahead);
    }
}

export const bgMusic = new BackgroundMusic();
